import React, { useState } from "react";
import { Zap, NotepadText } from "lucide-react";
import { createNewPermission } from "../../../services/team";
import { Button } from "../../Ui_Components/Button";

type PermissionType = "action" | "content";
type OperationKey = "create" | "edit" | "view" | "delete" | "duplicate";

const OPERATIONS: { key: OperationKey; label: string }[] = [
  { key: "create",    label: "Create"    },
  { key: "edit",      label: "Edit"      },
  { key: "view",      label: "View"      },
  { key: "delete",    label: "Delete"    },
  { key: "duplicate", label: "Duplicate" },
];

const toResource = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

interface CreatedActionPermission {
  resource: string;
  action: string;
  permission_type: "action";
  label?: string;
}

interface CreatedContentPermission {
  resource: string;
  action: string;
  permission_type: "content";
}

export interface NewPermissionResult {
  kind: PermissionType;
  permission?: CreatedActionPermission;
  permissions?: CreatedContentPermission[];
}

/* ── Checkbox ── */
const Checkbox: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({
  checked, onChange, label,
}) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onChange}
      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
        checked ? "bg-[#0DB5B5] border-[#0DB5B5]" : "bg-white border-[#D1D5DB]"
      }`}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
    <span className="text-sm 3xl:text-[16px] font-inter text-[#1F1F1F]">{label}</span>
  </label>
);

/* ── Type card ── */
const TypeCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ selected, onClick, icon, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left p-4 rounded-lg border transition-colors ${
      selected
        ? "border-[#0DB5B5] bg-[#F1FBFB]"
        : "border-[#E8E8E8] bg-white hover:border-[#D1D5DB]"
    }`}
  >
    <div className="flex items-start gap-3">
      <div
        className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
          selected ? "bg-[#E5F8F8]" : "bg-[#F3F4F6]"
        }`}
      >
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm 3xl:text-[16px] font-semibold font-inter text-[#1F1F1F]">
          {title}
        </p>
        <p className="text-xs 3xl:text-[13px] font-inter text-[#8D8D8D] mt-0.5 leading-snug">
          {description}
        </p>
      </div>
    </div>
  </button>
);

/* ── New Permission Modal ── */
const NewPermissionModal: React.FC<{
  onClose: () => void;
  onAdded: (result: NewPermissionResult) => void;
}> = ({ onClose, onAdded }) => {
  const [type, setType] = useState<PermissionType>("action");
  const [name, setName] = useState("");
  const [operations, setOperations] = useState<Set<OperationKey>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const toggleOperation = (op: OperationKey) => {
    setOperations((prev) => {
      const next = new Set(prev);
      if (next.has(op)) next.delete(op);
      else next.add(op);
      return next;
    });
  };

  const trimmedName = name.trim();
  const canAdd =
    trimmedName.length > 0 &&
    (type === "action" || operations.size > 0) &&
    !submitting;

  const handleAdd = async () => {
    if (!canAdd) return;

    const resource = toResource(trimmedName);
    if (!resource) {
      setSubmitError("Please enter a valid name");
      return;
    }

    const body =
      type === "action"
        ? { kind: "action", resource, action_name: trimmedName }
        : { kind: "content", resource, operations: Array.from(operations) };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await createNewPermission(body);
      if (data.success !== "1") {
        throw new Error(data.message || `Create failed`);
      }
      onAdded({
        kind: type,
        permission: data.permission,
        permissions: data.permissions,
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create permission",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const placeholder = type === "action" ? "eg. Export reports" : "eg. Recipe library";
  const nameLabel  = type === "action" ? "Action name" : "Content type name";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
            New permission
          </h2>
        </div>
        <div className="border-t border-[#E8E8E8] mx-6" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Type selector */}
          <div className="grid grid-cols-2 gap-4">
            <TypeCard
              selected={type === "action"}
              onClick={() => setType("action")}
              icon={
                <Zap
                  className={`w-4 h-4 ${type === "action" ? "text-[#0DB5B5]" : "text-[#646464]"}`}
                  strokeWidth={2}
                />
              }
              title="Action"
              description={`A single capability thats on or off : like "Exports reports"`}
            />
            <TypeCard
              selected={type === "content"}
              onClick={() => setType("content")}
              icon={
                <NotepadText
                  className={`w-4 h-4 ${type === "content" ? "text-[#0DB5B5]" : "text-[#646464]"}`}
                  strokeWidth={2}
                />
              }
              title="Content type"
              description={`A subject with create/edit/view : like "Recipe library"`}
            />
          </div>

          {/* Name input */}
          <div>
            <label className="block text-sm 3xl:text-[16px] font-medium font-inter text-[#1F1F1F] mb-2">
              {nameLabel}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={placeholder}
              className="w-full px-4 py-2.5 border border-[#E8E8E8] rounded-lg text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#0DB5B5] transition-colors"
            />
          </div>

          {/* Operations (content type only) */}
          {type === "content" && (
            <div>
              <p className="text-sm 3xl:text-[16px] font-inter text-[#646464] mb-3">
                Operations this content type supports
              </p>
              <div className="flex items-center gap-6 flex-wrap">
                {OPERATIONS.map((op) => (
                  <Checkbox
                    key={op.key}
                    checked={operations.has(op.key)}
                    onChange={() => toggleOperation(op.key)}
                    label={op.label}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#E8E8E8] mx-6" />

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-end gap-3">
          {submitError && (
            <span className="text-sm font-inter text-red-500 mr-auto">{submitError}</span>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleAdd}
            disabled={!canAdd}
          >
            {submitting ? "Adding..." : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewPermissionModal;
